<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useAppStore } from '../stores/app';
import { skillCategories } from '../data/portfolio';
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';

Chart.register(
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const appStore = useAppStore();
const chartCanvas = ref<HTMLCanvasElement | null>(null);
const isVisible = ref(false);
let chartInstance: Chart | null = null;

const activeCategory = ref(skillCategories[0].id);

const currentCategory = computed(() => 
  skillCategories.find(cat => cat.id === activeCategory.value)
);

const radarData = computed(() => {
  const labels = skillCategories.map(cat => 
    appStore.isZh ? cat.name : cat.nameEn
  );
  const data = skillCategories.map(cat => {
    const avg = cat.skills.reduce((sum, s) => sum + s.level, 0) / cat.skills.length;
    return Math.round(avg);
  });
  return { labels, data };
});

const techStack = [
  { name: 'Java', icon: 'devicon-java-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'Spring Boot', icon: 'devicon-spring-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'Flutter', icon: 'devicon-flutter-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'Next.js', icon: 'devicon-nextjs-original-wordmark', url: 'https://github.com/ctkqiang' },
  { name: 'Vue', icon: 'devicon-vuejs-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'C/C++', icon: 'devicon-cplusplus-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'Go', icon: 'devicon-go-plain-wordmark', url: 'https://github.com/ctkqiang' },
  { name: 'Python', icon: 'devicon-python-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'Erlang', icon: 'devicon-erlang-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'TypeScript', icon: 'devicon-typescript-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'Docker', icon: 'devicon-docker-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'Kubernetes', icon: 'devicon-kubernetes-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'MongoDB', icon: 'devicon-mongodb-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'MySQL', icon: 'devicon-mysql-plain colored', url: 'https://github.com/ctkqiang' },
  { name: 'TensorFlow', icon: 'devicon-tensorflow-original colored', url: 'https://github.com/ctkqiang' },
  { name: 'PyTorch', icon: 'devicon-pytorch-plain colored', url: 'https://github.com/ctkqiang' }
];

function createChart() {
  if (!chartCanvas.value) return;
  
  if (chartInstance) {
    chartInstance.destroy();
  }

  const ctx = chartCanvas.value.getContext('2d');
  if (!ctx) return;

  const gradient = ctx.createRadialGradient(150, 150, 0, 150, 150, 150);
  gradient.addColorStop(0, 'rgba(255, 107, 26, 0.4)');
  gradient.addColorStop(1, 'rgba(230, 57, 70, 0.15)');

  chartInstance = new Chart(ctx, {
    type: 'radar',
    data: {
      labels: radarData.value.labels,
      datasets: [{
        label: appStore.isZh ? '技能熟练度' : 'Skill Proficiency',
        data: radarData.value.data,
        backgroundColor: gradient,
        borderColor: '#FFB627',
        borderWidth: 2,
        pointBackgroundColor: '#FFD60A',
        pointBorderColor: '#FF6B1A',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 8
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 2000,
        easing: 'easeOutQuart'
      },
      scales: {
        r: {
          beginAtZero: true,
          max: 100,
          ticks: {
            stepSize: 20,
            color: 'rgba(255, 255, 255, 0.3)',
            backdropColor: 'transparent',
            font: { size: 10 }
          },
          grid: {
            color: 'rgba(255, 107, 26, 0.15)'
          },
          angleLines: {
            color: 'rgba(255, 107, 26, 0.15)'
          },
          pointLabels: {
            color: '#FFB627',
            font: {
              size: 13,
              family: 'ZCOOL KuaiLe',
              weight: 700
            }
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(5, 5, 5, 0.9)',
          titleColor: '#FFD60A',
          bodyColor: '#FFFAF0',
          borderColor: '#FF6B1A',
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8
        }
      }
    }
  });
}

const progressBars = ref<{ [key: string]: number }>({});

function animateProgressBars() {
  if (!currentCategory.value) return;
  
  currentCategory.value.skills.forEach((skill, index) => {
    setTimeout(() => {
      progressBars.value[skill.name] = 0;
      const interval = setInterval(() => {
        if (progressBars.value[skill.name] < skill.level) {
          progressBars.value[skill.name] += 1;
        } else {
          clearInterval(interval);
        }
      }, 20);
    }, index * 150);
  });
}

watch(activeCategory, () => {
  progressBars.value = {};
  setTimeout(animateProgressBars, 100);
});

watch(() => appStore.language, () => {
  if (chartInstance && isVisible.value) {
    createChart();
  }
});

onMounted(() => {
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          isVisible.value = true;
          setTimeout(() => {
            createChart();
            animateProgressBars();
          }, 300);
          observer.disconnect();
        }
      });
    },
    { threshold: 0.2 }
  );

  const section = document.getElementById('skills');
  if (section) {
    observer.observe(section);
  }
});
</script>

<template>
  <section id="skills" class="skills-section section">
    <div class="container">
      <h2 class="section-title">
        <i class="fas fa-tree"></i>
        {{ appStore.isZh ? '技能树' : 'Skill Tree' }}
      </h2>
      <div class="section-divider"></div>
      <p class="section-subtitle">
        {{ appStore.isZh 
          ? '吾之技如法宝，各有所长。前端如混天绫灵动多变，后端似火尖枪刚猛有力，数据若乾坤圈包罗万象' 
          : 'My skills are like magical treasures — frontend as fluid as the Red Armillary Sash, backend as fierce as the Fire-Tipped Spear, data as vast as the Universe Ring' }}
      </p>

      <div class="skills-content">
        <div class="chart-container glass-card">
          <h3 class="chart-title">
            <i class="fas fa-chart-radar"></i>
            {{ appStore.isZh ? '技能熟练度雷达图' : 'Skill Radar' }}
          </h3>
          <div class="chart-wrapper">
            <div class="chart-glow"></div>
            <canvas ref="chartCanvas"></canvas>
          </div>
        </div>

        <div class="skills-list-area">
          <div class="category-tabs">
            <button
              v-for="cat in skillCategories"
              :key="cat.id"
              class="category-tab"
              :class="{ active: activeCategory === cat.id }"
              @click="activeCategory = cat.id"
            >
              <i :class="['fas', cat.icon]"></i>
              <span>{{ appStore.isZh ? cat.name : cat.nameEn }}</span>
            </button>
          </div>

          <div class="skills-list glass-card" v-if="currentCategory">
            <h3 class="list-title">
              <i :class="['fas', currentCategory.icon]"></i>
              {{ appStore.isZh ? currentCategory.name : currentCategory.nameEn }}
            </h3>
            
            <div class="skill-items">
              <div 
                v-for="skill in currentCategory.skills" 
                :key="skill.name"
                class="skill-item"
              >
                <div class="skill-header">
                  <span class="skill-name">{{ skill.name }}</span>
                  <span class="skill-level">{{ progressBars[skill.name] || 0 }}%</span>
                </div>
                <div class="skill-bar">
                  <div 
                    class="skill-bar-fill"
                    :style="{ width: `${progressBars[skill.name] || 0}%` }"
                  >
                    <div class="skill-bar-glow"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="tech-stack-section">
        <h3 class="tech-stack-title">
          <i class="fas fa-tools"></i>
          {{ appStore.isZh ? '开发工具与技术栈' : 'Tools & Technologies' }}
        </h3>
        <div class="tech-stack-grid">
          <a
            v-for="tech in techStack"
            :key="tech.name"
            :href="tech.url"
            target="_blank"
            rel="noopener noreferrer"
            class="tech-card"
          >
            <i :class="tech.icon"></i>
            <span class="tech-name">{{ tech.name }}</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@import '../styles/components/skills.css';
</style>
